/**
 * ExportButton.jsx - Componente de Exportação PDF/Excel
 * CG Business ERP - P5
 * 
 * Botão reutilizável para exportar dados em PDF ou Excel
 * Respeita os filtros globais aplicados
 */

import { useState } from 'react';
import { FileDown, FileSpreadsheet, FileText, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { getStoredAuthToken } from '@/lib/authStorage';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * ExportButton - Dropdown button for PDF/Excel export
 * 
 * @param {string} module - Module name (dashboard, sales, expenses, cash)
 * @param {object} filters - Current filters to apply to export
 * @param {string} label - Button label (default: "Exportar")
 * @param {string} variant - Button variant
 * @param {string} size - Button size
 */
export function ExportButton({ 
  module, 
  filters = {}, 
  label = "Exportar",
  variant = "outline",
  size = "sm"
}) {
  const [exporting, setExporting] = useState(null); // 'pdf' or 'excel'
  
  // Build query string from filters
  const buildQueryString = () => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (typeof value === 'object') return;
      params.append(key, String(value));
    });
    
    return params.toString();
  };
  
  // Handle export
  const handleExport = async (format) => {
    setExporting(format);
    
    try {
      const token = getStoredAuthToken();
      const queryString = buildQueryString();
      const url = `${API_URL}/export/${module}/${format}${queryString ? '?' + queryString : ''}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Erro na exportação' }));
        throw new Error(error.detail || 'Erro ao exportar');
      }
      
      // Get filename from header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `export_${module}_${new Date().toISOString().slice(0,10)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${format.toUpperCase()} exportado com sucesso!`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Erro ao exportar. Verifique os filtros aplicados.');
    } finally {
      setExporting(null);
    }
  };
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          disabled={exporting !== null}
          data-testid={`${module}-export-button`}
        >
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileDown className="h-4 w-4 mr-2" />
          )}
          {exporting ? 'A exportar...' : label}
          <ChevronDown className="h-4 w-4 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>Formato de Exportação</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={() => handleExport('pdf')}
          disabled={exporting !== null}
          data-testid={`${module}-export-pdf`}
        >
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          <span>PDF</span>
          <span className="ml-auto text-xs text-muted-foreground">.pdf</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleExport('excel')}
          disabled={exporting !== null}
          data-testid={`${module}-export-excel`}
        >
          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
          <span>Excel</span>
          <span className="ml-auto text-xs text-muted-foreground">.xlsx</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportButton;
